import React, { useEffect, useState } from 'react';
import { tipsService } from '../../../../src/services/tipsService';

const TipsDashboardPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [receivedTips, setReceivedTips] = useState<any[]>([]);
  const [topTippers, setTopTippers] = useState<any[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [hist, top, ana] = await Promise.all([
          tipsService.history({ type: 'received', limit: 20, offset: 0 }),
          tipsService.topTippers({ limit: 5, days: 30 }),
          tipsService.analytics({ interval: 'day' }),
        ]);
        setReceivedTips(hist.tips || []);
        setTopTippers(top.topTippers || []);
        setAnalytics(ana || null);
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Failed to load tips');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Tips</h1>
        <p className="text-gray-600">Overview of tips you’ve received and your recent supporters.</p>
      </div>

      {loading && <div className="text-gray-600">Loading…</div>}
      {error && <div className="text-red-600">{error}</div>}

      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Total tips</div>
            <div className="text-2xl font-semibold">{analytics.totalAmount?.toFixed?.(2) ?? analytics.totalAmount}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Tip count</div>
            <div className="text-2xl font-semibold">{analytics.totalCount}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Average</div>
            <div className="text-2xl font-semibold">{analytics.average?.toFixed?.(2) ?? analytics.average}</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-3">Top Tippers (30 days)</h2>
          <ul className="divide-y">
            {topTippers.map((t, i) => (
              <li key={i} className="py-2 flex items-center justify-between">
                <span className="text-gray-700">{t.userId?.slice(0,8) || 'Anonymous'}</span>
                <span className="font-medium">${'{'}t.totalAmount?.toFixed?.(2) ?? t.totalAmount{'}'}</span>
              </li>
            ))}
            {topTippers.length === 0 && <li className="py-2 text-gray-500">No tippers yet.</li>}
          </ul>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-3">Recent Tips Received</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2">From</th>
                <th className="py-2">Amount</th>
                <th className="py-2">Status</th>
                <th className="py-2">Date</th>
              </tr>
            </thead>
            <tbody>
              {receivedTips.map((tip) => (
                <tr key={tip.id} className="border-t">
                  <td className="py-2">{tip.isAnonymous ? 'Anonymous' : (tip.fromUser?.username || tip.fromUserId?.slice(0,6))}</td>
                  <td className="py-2">{new Intl.NumberFormat('en-US',{ style:'currency', currency: tip.currency || 'USD'}).format(Number(tip.amount || 0))}</td>
                  <td className="py-2">{tip.status}</td>
                  <td className="py-2">{new Date(tip.createdAt).toLocaleString()}</td>
                </tr>
              ))}
              {receivedTips.length === 0 && (
                <tr><td colSpan={4} className="py-4 text-gray-500">No tips yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TipsDashboardPage;
